/**
 * @reflecto/core — crypto.ts
 *
 * All encryption/decryption for Reflecto lives here.
 * Uses TweetNaCl (X25519 + XSalsa20-Poly1305) to be byte-identical
 * with Lazysodium on Android.
 *
 * RULE: server never sees plaintext. Encrypt before sending, decrypt after receiving.
 */

import nacl from "tweetnacl";
import { decodeBase64, encodeBase64 } from "tweetnacl-util";

export const WIRE_VERSION = 0x01;

/**
 * Minimum legal decoded length of a `[0x01 | nonce | ciphertext]` wire blob:
 * version byte + nonce + one Poly1305 tag. That is the length `encryptMessage`
 * produces for empty plaintext, so it is a floor no real blob falls below.
 *
 * Exported so validators outside this module can reject malformed blobs before
 * they are stored. Do NOT re-derive the arithmetic elsewhere: it is computed
 * from `nacl.box`'s own constants and adapts if TweetNaCl changes them, while
 * a hand-written copy would silently go stale — and this file's fixed-vector
 * interop tests, which never see the copy, would stay green throughout.
 */
export const MIN_WIRE_LENGTH = 1 + nacl.box.nonceLength + nacl.box.overheadLength;

export interface KeyPair {
  publicKey: Uint8Array;
  secretKey: Uint8Array;
}

/**
 * Generate a new X25519 keypair.
 */
export function generateKeyPair(): KeyPair {
  return nacl.box.keyPair();
}

/**
 * Reject a peer public key that drives the X25519 exchange to the all-zero point.
 *
 * TweetNaCl's `crypto_scalarmult` has NO failure signal — it returns the shared-secret
 * bytes, and for an all-zero or low-order peer key those bytes are all zeros. `nacl.box`
 * then hashes that into a shared key and emits a perfectly well-formed ciphertext under
 * a secret that depends on NEITHER party's secret key, so any observer can compute it.
 * libsodium's `crypto_box_easy` throws on the same input, which means Android
 * (Lazysodium) has always failed CLOSED here while TypeScript failed OPEN. RFC 7748
 * makes the check a MAY, so neither library is non-conformant — the two implementations
 * made opposite permitted choices and ours was the wrong one.
 *
 * The check is on the scalarmult OUTPUT, deliberately, not on a return value (there is
 * none) and not against a hardcoded small-order blacklist (which can omit a point).
 *
 * Cost: one extra scalarmult per encrypt/decrypt (~tens of microseconds). That is paid
 * on a per-message path, not a per-byte one, and buys a fail-closed boundary.
 *
 * Regression coverage: `src/__tests__/cryptoLowOrder.test.ts`.
 */
function assertUsablePeerKey(peerPublicKey: Uint8Array, ourSecretKey: Uint8Array): void {
  const shared = nacl.scalarMult(ourSecretKey, peerPublicKey);
  // Accumulate over all 32 bytes rather than early-exiting on the first non-zero, so
  // the loop's duration does not reveal WHICH byte differed. The accept/reject branch
  // below is still data-dependent, and deliberately so — it is the whole point of the
  // function, and a peer's public key is not secret from the peer anyway.
  let bits = 0;
  for (const byte of shared) {
    bits |= byte;
  }
  if (bits === 0) {
    throw new Error(
      "Invalid public key: low-order or all-zero point (shared secret would be public)"
    );
  }
}

/**
 * Non-throwing intake check: is this a public key we could ever hold a private
 * conversation with?
 *
 * For validating a key at the moment it ARRIVES — pairing, registration — so a
 * degenerate key is refused before it is ever persisted, rather than only failing later
 * at every encrypt. Storing one turns that device into a poison pill for any batched
 * send that includes it.
 *
 * No secret key is needed, and that is not a shortcut: X25519 clamps every scalar to a
 * multiple of the cofactor, so a low-order point collapses to the all-zero output under
 * ANY scalar. A fixed non-secret probe therefore answers the question exactly. Verified
 * empirically against all seven canonical small-order points, under five random scalars,
 * with zero false positives over 200 honest keypairs.
 */
export function isUsablePublicKey(publicKey: Uint8Array): boolean {
  if (publicKey.length !== nacl.box.publicKeyLength) {
    return false;
  }
  // Arbitrary, fixed, and NOT secret — see above for why the value cannot matter.
  const probe = new Uint8Array(nacl.box.secretKeyLength).fill(9);
  const shared = nacl.scalarMult(probe, publicKey);
  let bits = 0;
  for (const byte of shared) {
    bits |= byte;
  }
  return bits !== 0;
}

/**
 * Encode a Uint8Array to base64 string (for storage/transport).
 */
export { decodeBase64, encodeBase64 };

/**
 * Encrypt a message from sender → recipient.
 *
 * Wire format: [0x01 (1 byte)] + [nonce (24 bytes)] + [ciphertext (variable)]
 *
 * @param plaintext   - raw bytes to encrypt
 * @param recipientPublicKey - recipient's X25519 public key
 * @param senderSecretKey    - sender's X25519 secret key
 * @param nonce              - 24-byte nonce (pass nacl.randomBytes(24) in production)
 */
export function encryptMessage(
  plaintext: Uint8Array,
  recipientPublicKey: Uint8Array,
  senderSecretKey: Uint8Array,
  nonce: Uint8Array = nacl.randomBytes(nacl.box.nonceLength)
): Uint8Array {
  if (recipientPublicKey.length !== nacl.box.publicKeyLength) {
    throw new Error(
      `Invalid public key: expected ${nacl.box.publicKeyLength} bytes, got ${recipientPublicKey.length}`
    );
  }
  if (senderSecretKey.length !== nacl.box.secretKeyLength) {
    throw new Error(
      `Invalid secret key: expected ${nacl.box.secretKeyLength} bytes, got ${senderSecretKey.length}`
    );
  }
  if (nonce.length !== nacl.box.nonceLength) {
    throw new Error(`Invalid nonce: expected ${nacl.box.nonceLength} bytes, got ${nonce.length}`);
  }
  assertUsablePeerKey(recipientPublicKey, senderSecretKey);

  const ciphertext = nacl.box(plaintext, nonce, recipientPublicKey, senderSecretKey);

  if (!ciphertext) {
    throw new Error("Encryption failed");
  }

  // Wire format: version (1) + nonce (24) + ciphertext
  const wire = new Uint8Array(1 + nonce.length + ciphertext.length);
  wire[0] = WIRE_VERSION;
  wire.set(nonce, 1);
  wire.set(ciphertext, 1 + nonce.length);
  return wire;
}

/**
 * Decrypt a wire-format blob.
 *
 * @param wire               - [0x01] + [nonce (24)] + [ciphertext]
 * @param senderPublicKey    - sender's X25519 public key
 * @param recipientSecretKey - recipient's X25519 secret key
 */
export function decryptMessage(
  wire: Uint8Array,
  senderPublicKey: Uint8Array,
  recipientSecretKey: Uint8Array
): Uint8Array {
  if (wire.length < MIN_WIRE_LENGTH) {
    throw new Error(
      `Wire blob too short: expected at least ${MIN_WIRE_LENGTH} bytes, got ${wire.length}`
    );
  }
  if (wire[0] !== WIRE_VERSION) {
    throw new Error(`Unsupported wire version: 0x${wire[0].toString(16)}`);
  }
  if (senderPublicKey.length !== nacl.box.publicKeyLength) {
    throw new Error(
      `Invalid public key: expected ${nacl.box.publicKeyLength} bytes, got ${senderPublicKey.length}`
    );
  }
  if (recipientSecretKey.length !== nacl.box.secretKeyLength) {
    throw new Error(
      `Invalid secret key: expected ${nacl.box.secretKeyLength} bytes, got ${recipientSecretKey.length}`
    );
  }
  // Guarded on decrypt too, not just encrypt: a low-order SENDER key makes the shared
  // secret public, so anyone could forge a box that authenticates. Rejecting the key is
  // what stops that being indistinguishable from a genuine message.
  assertUsablePeerKey(senderPublicKey, recipientSecretKey);

  const nonce = wire.slice(1, 1 + nacl.box.nonceLength);
  const ciphertext = wire.slice(1 + nacl.box.nonceLength);

  const plaintext = nacl.box.open(ciphertext, nonce, senderPublicKey, recipientSecretKey);

  if (!plaintext) {
    throw new Error("Decryption failed — wrong keys or tampered ciphertext");
  }

  return plaintext;
}
